import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const ITDesktopSupport = () => {
  // Hero Banner Section
  const HeroBannerSection = () => (
    <section className="py-20 bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 relative overflow-hidden">
      <div className="absolute inset-0 opacity-20">
        <img 
          src="https://images.unsplash.com/photo-1556761175-b413da4baf72?w=1920" 
          alt="IT Support" 
          className="w-full h-full object-cover"
        />
      </div>
      <div className="max-w-7xl mx-auto px-4 relative z-10 text-center">
        <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
          Reliable <span className="text-green-500">IT & Desktop Support</span> Services
        </h1>
        <p className="text-white text-xl max-w-3xl mx-auto">
          Keep your technology running smoothly with our 24/7 IT support services. From help desk to system maintenance, we ensure minimal downtime and maximum productivity.
        </p>
      </div>
    </section>
  );

  // Overview Section
  const OverviewSection = () => (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <img 
              src="https://images.unsplash.com/photo-1531482615713-2afd69097998?w=800&h=600&fit=crop" 
              alt="IT Support" 
              className="rounded-lg shadow-2xl w-full"
            />
          </div>
          <div>
            <h2 className="text-4xl font-bold mb-6">
              Expert IT Support for <span className="text-green-600">Your Business</span>
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              At Plexgate, we provide comprehensive IT and desktop support services to keep your technology infrastructure running smoothly. Our experienced support team is available 24/7 to resolve technical issues quickly and efficiently, minimizing downtime and maximizing productivity.
            </p>
            <p className="text-gray-600 leading-relaxed">
              Whether you need help desk support, system maintenance, hardware troubleshooting, or software assistance, we're here to help. We provide both remote and on-site support tailored to your business needs, ensuring your team can focus on what they do best.
            </p>
          </div>
        </div>
      </div>
    </section>
  );

  // Services Section
  const ServicesSection = () => {
    const services = [
      {
        title: '24/7 Help Desk Support',
        description: 'Round-the-clock technical support via phone, email, and chat. Quick response times and efficient issue resolution.',
        icon: '📞'
      },
      {
        title: 'Desktop & Laptop Support',
        description: 'Hardware troubleshooting, software installation, system optimization, and repairs for all desktop and laptop computers.',
        icon: '💻'
      },
      {
        title: 'Network Support',
        description: 'Network troubleshooting, connectivity issues, router configuration, WiFi optimization, and network security.',
        icon: '🌐'
      },
      {
        title: 'Software Troubleshooting',
        description: 'Application errors, software updates, license management, and compatibility issues across all platforms.',
        icon: '⚙️'
      },
      {
        title: 'Remote Support',
        description: 'Secure remote desktop support to quickly diagnose and resolve issues without the need for on-site visits.',
        icon: '🖥️'
      },
      {
        title: 'System Maintenance',
        description: 'Regular updates, security patches, performance optimization, and preventive maintenance to avoid downtime.',
        icon: '🔧'
      }
    ];

    return (
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              Our <span className="text-green-600">IT Support</span> Services
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Comprehensive technical support to keep your business running smoothly
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-lg p-8 shadow-lg hover:shadow-xl transition-shadow">
                <div className="text-5xl mb-4">{service.icon}</div>
                <h3 className="text-xl font-bold mb-3">{service.title}</h3>
                <p className="text-gray-600">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // Support Tiers Section
  const SupportTiersSection = () => {
    const tiers = [
      {
        name: 'Basic Support',
        features: ['Email & Phone Support', 'Business Hours Coverage', 'Remote Assistance', 'Issue Tracking', 'Knowledge Base Access'],
        icon: '🥉'
      },
      {
        name: 'Standard Support',
        features: ['24/7 Help Desk', 'Priority Response', 'On-Site Support', 'System Monitoring', 'Monthly Reports'],
        icon: '🥈',
        popular: true
      },
      {
        name: 'Premium Support',
        features: ['Dedicated Support Team', 'Proactive Monitoring', 'Unlimited On-Site Visits', 'Strategic IT Planning', 'VIP Support'],
        icon: '🥇'
      }
    ];

    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              Choose Your <span className="text-green-600">Support Level</span>
            </h2>
            <p className="text-gray-600">Flexible support packages to match your business needs</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {tiers.map((tier, index) => (
              <div key={index} className={`bg-gray-50 rounded-lg p-8 ${tier.popular ? 'ring-4 ring-green-500 shadow-xl' : 'shadow-lg'}`}>
                {tier.popular && (
                  <div className="bg-green-500 text-white text-sm font-bold px-4 py-1 rounded-full inline-block mb-4">
                    MOST POPULAR
                  </div>
                )}
                <div className="text-5xl mb-4">{tier.icon}</div>
                <h3 className="text-2xl font-bold mb-6">{tier.name}</h3>
                <ul className="space-y-3">
                  {tier.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-green-500 mt-1">✓</span>
                      <span className="text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // How We Help Section
  const HowWeHelpSection = () => {
    const benefits = [
      {
        title: 'Fast Response Times',
        description: 'Average response time under 15 minutes for critical issues, ensuring minimal disruption to your business.',
        icon: '⚡'
      },
      {
        title: 'Expert Technicians',
        description: 'Certified IT professionals with extensive experience across all major platforms and technologies.',
        icon: '👨‍💻'
      },
      {
        title: 'Proactive Monitoring',
        description: 'Continuous system monitoring to identify and resolve issues before they impact your business.',
        icon: '📊'
      },
      {
        title: 'Cost-Effective',
        description: 'Predictable monthly costs with no hidden fees, more affordable than hiring full-time IT staff.',
        icon: '💰'
      },
      {
        title: 'Documentation',
        description: 'Detailed records of all support tickets, resolutions, and system changes for accountability.',
        icon: '📝'
      },
      {
        title: 'User Training',
        description: 'We educate your team on best practices and common issues to improve overall IT literacy.',
        icon: '🎓'
      }
    ];

    return (
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              Why Choose <span className="text-green-600">Plexgate IT Support</span>?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex gap-4">
                <div className="text-4xl">{benefit.icon}</div>
                <div>
                  <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // Common Issues Section
  const CommonIssuesSection = () => {
    const issues = [
      'Computer running slow or freezing',
      'Internet connectivity problems',
      'Email setup and configuration',
      'Printer and peripheral issues',
      'Software installation and updates',
      'Password resets and access issues',
      'Data backup and recovery',
      'Virus and malware removal',
      'Hardware failures and repairs',
      'VPN and remote access setup'
    ];

    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              Common Issues <span className="text-green-600">We Resolve</span>
            </h2>
            <p className="text-gray-600">No problem is too big or too small for our team</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 max-w-5xl mx-auto">
            {issues.map((issue, index) => (
              <div key={index} className="flex items-start gap-3 bg-gray-50 p-4 rounded-lg">
                <span className="text-green-500 text-xl mt-1">✓</span>
                <span className="text-gray-700">{issue}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };  // CTA Section
  const CTASection = () => (
    <section className="py-16 bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 relative overflow-hidden">
      <div className="absolute inset-0 opacity-20">
        <img 
          src="https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=1920" 
          alt="Team" 
          className="w-full h-full object-cover"
        />
      </div>
      <div className="max-w-7xl mx-auto px-4 relative z-10 text-center">
        <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
          Get Expert IT Support <span className="text-green-500">Today</span>
        </h2>
        <p className="text-white text-xl mb-8 max-w-3xl mx-auto">
          Stop struggling with technical issues. Let our expert team handle your IT support so you can focus on growing your business.
        </p>
        <div className="flex gap-4 justify-center">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 bg-green-500 text-white px-8 py-3 rounded-full hover:bg-green-600 transition-colors font-medium"
          >
            Get Support Now →
          </Link>
          <Link
            to="/services"
            className="inline-flex items-center gap-2 bg-white text-blue-900 px-8 py-3 rounded-full hover:bg-gray-100 transition-colors font-medium"
          >
            View All Services
          </Link>
        </div>
      </div>
    </section>
  );

  return (
    <div className="it-desktop-support-page">
      <HeroBannerSection />
      <OverviewSection />
      <ServicesSection />
      <SupportTiersSection />
      <HowWeHelpSection />
      <CommonIssuesSection />
      <FAQSection />
      <CTASection />
    </div>
  );
};



  // FAQ Section
  const FAQSection = () => {
  const [expandedFAQ, setExpandedFAQ] = useState(null);
    const faqs = [
      {
        question: 'What are your support hours?',
        answer: 'We offer 24/7 support for critical issues on Standard and Premium plans. Basic plan includes support during business hours (9 AM - 6 PM EST, Monday-Friday).'
      },
      {
        question: 'How quickly do you respond to support requests?',
        answer: 'Critical issues: within 15 minutes. High priority: within 1 hour. Normal priority: within 4 hours. We prioritize based on business impact and severity.'
      },
      {
        question: 'Do you provide on-site support?',
        answer: 'Yes! On-site support is available for Standard and Premium plans when remote support isn\'t sufficient. We can dispatch technicians to your location as needed.'
      },
      {
        question: 'Can you support Mac and Windows computers?',
        answer: 'Absolutely! Our team is experienced with Windows, macOS, Linux, and all major operating systems and software applications.'
      },
      {
        question: 'What if we need support outside business hours?',
        answer: 'Our 24/7 support is available for urgent issues that impact business operations. We also offer emergency support add-ons for Basic plan customers.'
      }
    ];

    return (
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">
              Frequently Asked <span className="text-green-600">Questions</span>
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                  className="w-full px-6 py-4 flex justify-between items-center text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-lg">{faq.question}</span>
                  <span className="text-2xl text-gray-600">
                    {expandedFAQ === index ? '−' : '+'}
                  </span>
                </button>
                {expandedFAQ === index && (
                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                    <p className="text-gray-600">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };


export default ITDesktopSupport;